import { addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { PaginatedTable } from '@/components/ui/PaginatedTable';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { textLinkVariants } from '@/components/ui/button-variants';
import { useOrg } from '@/contexts/OrgContext';
import { useFirestorePageQuery } from '@/hooks/useFirestorePageQuery';
import { usePartyNames } from '@/hooks/usePartyNames';
import { getFirestoreDb } from '@/lib/firebase';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

type Invoice = {
  id: string;
  date: string;
  status: string;
  grossTotal?: string;
  balanceDue?: string;
  customerId?: string;
};

type Customer = { id: string; code: string; name: string; balance: string };

export function SalesPage() {
  const { t } = useTranslation();
  const { orgId, currentPeriodId } = useOrg();
  const online = useOnlineStatus();
  const [tab, setTab] = useState<'invoices' | 'customers'>('invoices');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const navigate = useNavigate();

  const mapInvoice = useCallback(
    (id: string, data: Record<string, unknown>) => ({ id, ...data }) as Invoice,
    []
  );
  const mapCustomer = useCallback(
    (id: string, data: Record<string, unknown>) => ({
      id,
      code: String(data.code ?? ''),
      name: String(data.name ?? ''),
      balance: String(data.balance ?? '0.00')
    }),
    []
  );

  const invoices = useFirestorePageQuery<Invoice>({
    orgId,
    collectionPath: 'salesInvoices',
    orderByField: 'updatedAt',
    orderDirection: 'desc',
    mapDoc: mapInvoice,
    enabled: tab === 'invoices'
  });

  const customers = useFirestorePageQuery<Customer>({
    orgId,
    collectionPath: 'customers',
    orderByField: 'name',
    orderDirection: 'asc',
    mapDoc: mapCustomer,
    enabled: tab === 'customers'
  });

  const customerIds = useMemo(
    () => invoices.rows.map((i) => i.customerId ?? '').filter(Boolean),
    [invoices.rows]
  );
  const custMap = usePartyNames(orgId, 'customers', customerIds);

  async function addCustomer() {
    if (!orgId || !online || !code.trim() || !name.trim()) return;
    await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'customers'), {
      code: code.trim(),
      name: name.trim(),
      balance: '0.00',
      active: true,
      createdAt: serverTimestamp()
    });
    setCode('');
    setName('');
    customers.refresh();
  }

  async function newInvoice() {
    if (!orgId || !currentPeriodId || !online) return;
    const first = customers.rows[0];
    if (!first) return;
    const today = new Date().toISOString().slice(0, 10);
    const ref = await addDoc(collection(getFirestoreDb(), 'organizations', orgId, 'salesInvoices'), {
      customerId: first.id,
      date: today,
      dueDate: today,
      periodId: currentPeriodId,
      status: 'draft',
      lines: [{ quantity: 1, unitPrice: '0.00', taxRatePercent: 0 }],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    navigate(`/sales/invoices/${ref.id}`);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold">{t('nav.salesAr')}</h1>
      <SegmentedControl
        className="mt-4 max-w-md"
        items={[
          { id: 'invoices', label: t('sales.invoices') },
          { id: 'customers', label: t('sales.customers') }
        ]}
        value={tab}
        onChange={(id) => setTab(id as 'invoices' | 'customers')}
      />

      {tab === 'customers' && (
        <div className="mt-6 space-y-4">
          <div className="flex flex-wrap gap-2">
            <input
              className="rounded border border-[var(--fv-border)] px-3 py-2 text-sm"
              placeholder={t('coa.code')}
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <input
              className="rounded border border-[var(--fv-border)] px-3 py-2 text-sm"
              placeholder={t('coa.name')}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <Button onClick={() => void addCustomer()} disabled={!online}>
              {t('sales.addCustomer')}
            </Button>
          </div>
          <PaginatedTable
            loading={customers.loading}
            pageIndex={customers.pageIndex}
            hasNext={customers.hasNext}
            hasPrev={customers.hasPrev}
            onNext={customers.nextPage}
            onPrev={customers.prevPage}
            pageSize={customers.pageSize}
            onPageSizeChange={customers.setPageSize}
            empty={
              !customers.rows.length ? (
                <p className="p-6 text-center text-sm text-[var(--fv-text-muted)]">
                  {t('common.noResults')}
                </p>
              ) : undefined
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--fv-bg)]">
                  <th className="p-2 text-start">{t('coa.code')}</th>
                  <th className="p-2 text-start">{t('coa.name')}</th>
                  <th className="p-2 text-end">{t('sales.arBalance')}</th>
                </tr>
              </thead>
              <tbody>
                {customers.rows.map((c) => (
                  <tr key={c.id} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{c.code}</td>
                    <td className="p-2">{c.name}</td>
                    <td className="p-2 text-end"><MoneyDisplay value={c.balance} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PaginatedTable>
        </div>
      )}

      {tab === 'invoices' && (
        <div className="mt-6">
          <Button onClick={() => void newInvoice()} disabled={!online || !customers.rows.length}>
            {t('sales.newInvoice')}
          </Button>
          <PaginatedTable
            className="mt-4"
            loading={invoices.loading}
            pageIndex={invoices.pageIndex}
            hasNext={invoices.hasNext}
            hasPrev={invoices.hasPrev}
            onNext={invoices.nextPage}
            onPrev={invoices.prevPage}
            pageSize={invoices.pageSize}
            onPageSizeChange={invoices.setPageSize}
            empty={
              !invoices.rows.length ? (
                <p className="p-6 text-center text-sm text-[var(--fv-text-muted)]">
                  {t('common.noResults')}
                </p>
              ) : undefined
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--fv-bg)]">
                  <th className="p-2 text-start">{t('journals.date')}</th>
                  <th className="p-2 text-start">{t('sales.customer')}</th>
                  <th className="p-2">{t('journals.status')}</th>
                  <th className="p-2 text-end">{t('sales.amount')}</th>
                  <th className="p-2" />
                </tr>
              </thead>
              <tbody>
                {invoices.rows.map((inv) => (
                  <tr key={inv.id} className="border-t border-[var(--fv-border)]">
                    <td className="p-2">{inv.date}</td>
                    <td className="p-2">
                      {custMap.get(inv.customerId ?? '') || inv.customerId || '—'}
                    </td>
                    <td className="p-2">{inv.status}</td>
                    <td className="p-2 text-end"><MoneyDisplay value={inv.grossTotal} /></td>
                    <td className="p-2">
                      <Link to={`/sales/invoices/${inv.id}`} className={textLinkVariants()}>
                        {t('sales.open')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PaginatedTable>
        </div>
      )}
    </div>
  );
}
